import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import './accreditation-sidebar-controller.scss';

import { catchRenderException, Endpoint, ProviderAccreditationStatusService } from '@philips-emr/hpms-provider-accreditation-commons';
import { DeckAPI, FetchAPI, FormAPI, PanelAPI, Stamp, StampIcon, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnChange, OnDynamicContentRender, OnMount, OnSelectItem, OnUnselectItem } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationSidebarMenuAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './accreditation-sidebar.css';

interface Data {
  id: number,
  name: string,
  pendencies: Array<any>,
}

enum AccreditationMenuType {
  ESTABLISHMENT = 1,
  ESTABLISHMENT_SPECIALTY = 2,
  SERVICES_AND_MATERIALS = 3,
  PACKAGES = 4,
  CLINICAL_STAFF = 5,
  PROFESSIONALS = 6,
  BEDS_AND_EQUIPMENTS = 7,
  QUALIFICATIONS = 8,
  COMPLEMENTARY_DATA = 9,
  DOCUMENTS = 10,
  CONCLUSION = 12,
}

@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION_SIDEBAR_MENU })
export default class AccreditationSidebarController extends BaseController {

  @Inject(Aliases.PANEL_SPECIALTY)
  private panelSpecialty: PanelAPI;
  @Inject(Aliases.PANEL_CLINICAL_STAFF)
  private panelClinicalStaff: PanelAPI;
  @Inject(Aliases.PANEL_CONCLUSION)
  private panelConclusion: PanelAPI;
  @Inject(Aliases.PANEL_DOCUMENTS)
  private panelDocuments: PanelAPI;
  @Inject(Aliases.PANEL_PROFESSIONALS)
  private panelProfessionals: PanelAPI;
  @Inject(Aliases.PANEL_SERVICES)
  private panelServices: PanelAPI;
  @Inject(Aliases.PANEL_BEDS)
  private panelBeds: PanelAPI;
  @Inject(Aliases.PANEL_COMP)
  private panelComp: PanelAPI;
  @Inject(Aliases.PANEL_ESTABLISHMENT)
  private panelEstablishment: PanelAPI;
  @Inject(Aliases.PANEL_PACKAGES)
  private panelPackages: PanelAPI;
  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;
  @Inject(Aliases.PANEL_QUALIFICATIONS)
  private panelQualifications: PanelAPI;
  @Inject(Aliases.PANEL_REFUSAL_TOAST)
  private panelToast: PanelAPI;
  @Inject(Aliases.DECK_MENU)
  private deckMenu: DeckAPI;

  private static DENIED = '6';

  @OnAfterFetch(Aliases.FETCH_MENUS_MOBILE)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as [{ pendencies: [], hideImage: boolean }];
    if (data) {
      data.forEach(item => item.hideImage = isEmptyArrayOrObject(item.pendencies));
      const record = [...data].shift();
      record && this.formMenuMobile.setRecord({ menuMobile: record });
    }
  }

  @OnAfterFetch(Aliases.FETCH_MENUS)
  onAfterFetchMenu(): void {
    this.deckMenu.selectCard(String(AccreditationMenuType.ESTABLISHMENT));
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<{ id: AccreditationMenuType }>): void {
    this.visibilityControl(event.value.id);
  }

  @OnMount([Aliases.FETCH_MENUS, Aliases.FETCH_MENUS_MOBILE])
  onMountMenu(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    const { nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    fetch.setParameters({ nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation });
    fetch.forceFetch();
  }

  @OnMount(Aliases.PANEL_ACCREDITATION)
  onMountAccreditation(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqUser as number).then((response) => {
      const { status } = response.body;
      if (status != '7') {
        api.setVisible(true);
      }
    });
  }

  @OnMount(Aliases.PANEL_CNES)
  onMountCnes(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqUser as number).then((response) => {
      const { status } = response.body;
      if (status == '7') {
        api.setVisible(true);
      }
    });
  }

  @OnSelectItem(Aliases.DECK_MENU)
  onselect(event: SelectItemEvent<{ id: AccreditationMenuType }>): void {
    const { id } = event.item;
    this.visibilityControl(id);
  }

  @OnUnselectItem(Aliases.DECK_MENU)
  onUnselect(event: SelectItemEvent<any>): void {
    const { id } = event.item;
    this.visibilityControl(id);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderField(event: RenderEvent): void {
    const { render } = event;
    const { pendencies } = event.data as unknown as Data;

    if (!isEmptyArrayOrObject(pendencies)) {
      render((): ReactElement => <Stamp icon={StampIcon.WARNING} color={Color.SAFETY_ORANGE} label="" />);
    }
  }

  @OnMount(Aliases.TOAST_NOTIFICATION_REFUSAL)
  onMountToastRefusal(event: MountEvent): void {
    const api = event.target as ToastNotificationAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    void ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqUser as number).then((response) => {
      const { status } = response.body;

      catchRenderException(() => this.panelToast.setVisible(status == AccreditationSidebarController.DENIED));

      if (status == AccreditationSidebarController.DENIED) {
        void HttpService.get<any>(Endpoint.GET.ACCREDITATION, { nrSeqUser, nrSeqAccreditation }).then(resp => {
          const body = resp.body;
          if (body) {
            const { refusalReason } = body;
            api.setMessage(refusalReason);
          }
        });
      }
    });
  }

  private visibilityControl(menu: number): void {
    this.panelEstablishment.setVisible(menu == AccreditationMenuType.ESTABLISHMENT);
    this.panelSpecialty.setVisible(menu == AccreditationMenuType.ESTABLISHMENT_SPECIALTY);
    this.panelServices.setVisible(menu == AccreditationMenuType.SERVICES_AND_MATERIALS);
    this.panelPackages.setVisible(menu === AccreditationMenuType.PACKAGES);
    this.panelClinicalStaff.setVisible(menu == AccreditationMenuType.CLINICAL_STAFF);
    this.panelProfessionals.setVisible(menu == AccreditationMenuType.PROFESSIONALS);
    this.panelBeds.setVisible(menu == AccreditationMenuType.BEDS_AND_EQUIPMENTS);
    this.panelQualifications.setVisible(menu == AccreditationMenuType.QUALIFICATIONS);
    this.panelComp.setVisible(menu == AccreditationMenuType.COMPLEMENTARY_DATA);
    this.panelDocuments.setVisible(menu == AccreditationMenuType.DOCUMENTS);
    this.panelConclusion.setVisible(menu == AccreditationMenuType.CONCLUSION);
  }

}
